import { useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useSession } from '@/shared/auth';
import { Icon } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';
import { editorPathFor, templateFor } from '@/shared/folders';
import type { FoldersNode } from '@/shared/folders';
import { useOrg } from '@/shared/org';
import { useHomeWorkspace } from './home/useHomeWorkspace';
import { LIBRARY_DOCUMENT_INFO } from './library/LibraryTab';
import { useLibraryRecents } from './library/libraryRecents';
import type { IconName } from '@/shared/components/ui/Icon';

/** One drawer link: a Folders node, or a Numtable library document (no star). */
interface SidebarItem {
  key: string;
  to: string;
  name: string;
  icon: IconName;
  node: FoldersNode | null;
}

const nodeItem = (node: FoldersNode): SidebarItem => ({
  key: node.id,
  to: editorPathFor(node.id, node.docType),
  name: node.name,
  icon: templateFor(node.docType)?.icon ?? 'sheets',
  node,
});

interface Props {
  /** Refetch whenever the drawer opens, so a document opened a moment ago is
   *  already in Last opened. */
  open: boolean;
  linkClassName: (state: { isActive: boolean }) => string;
}

/**
 * The drawer's document lists — Starred, Shared and Last opened —
 * in the order Home shows them. Modules stay reachable through Apps.
 */
export function SidebarDocuments({ open, linkClassName }: Props) {
  const { t } = useI18n();
  const org = useOrg();
  const session = useSession();
  const orgId = org.status === 'ready' ? org.orgId : null;
  const userId = session.session?.user.id ?? null;
  const canStar = org.status === 'ready' && !org.isViewingAs && userId !== null;
  const workspace = useHomeWorkspace(orgId, userId);
  const { reload } = workspace;
  const libraryRecents = useLibraryRecents();

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  // Same rule as Home: a share stops being new once opened since it was shared.
  const newShared = useMemo(
    () =>
      workspace.shared
        .filter(({ sharedAt, openedAt }) => !openedAt || Date.parse(openedAt) < Date.parse(sharedAt))
        .map(({ node }) => node),
    [workspace.shared],
  );

  // Starred documents already have their own list above. Library documents
  // opened in this browser join by date, as on Home.
  const recentItems = [
    ...workspace.recent
      .filter(({ node }) => !workspace.starredIds.has(node.id))
      .map(({ node, openedAt }) => ({ openedAt, item: nodeItem(node) })),
    ...libraryRecents.map(({ id, openedAt }) => {
      const doc = LIBRARY_DOCUMENT_INFO.find((d) => d.id === id)!;
      return { openedAt, item: { key: `library:${id}`, to: `/library/${id}`, name: t(doc.titleKey), icon: doc.icon, node: null } };
    }),
  ]
    .sort((a, b) => Date.parse(b.openedAt) - Date.parse(a.openedAt))
    .map(({ item }) => item);

  const sections: { key: string; title: string; items: readonly SidebarItem[] }[] = [
    { key: 'starred', title: t('home.starred'), items: workspace.starred.map(nodeItem) },
    { key: 'shared', title: t('home.sharedWithMe'), items: newShared.map(nodeItem) },
    { key: 'recent', title: t('home.lastOpened'), items: recentItems },
  ];

  return (
    <>
      {sections
        .filter((section) => section.items.length > 0)
        .map((section) => (
          <section key={section.key} className="mt-3 flex flex-col gap-0.5">
            <h2 className="px-3 pb-1 text-[0.6875rem] font-medium uppercase tracking-wide text-muted">
              {section.title}
            </h2>
            {section.items.map(({ key, to, name, icon, node }) => (
              <div key={key} className="group relative">
                <NavLink
                  to={to}
                  className={(state) => `${linkClassName(state)}${canStar && node ? ' pr-10' : ''}`}
                  title={name}
                >
                  <Icon name={icon} className="h-4 w-4 shrink-0" />
                  <span className="truncate">{name}</span>
                </NavLink>
                {/* Same star as Home: lit when starred, otherwise shown on hover
                    (always on touch screens, which have no hover). */}
                {canStar && node && (() => {
                  const starred = workspace.starredIds.has(node.id);
                  return (
                    <button
                      type="button"
                      aria-label={t(starred ? 'folders.unstar' : 'folders.star')}
                      aria-pressed={starred}
                      onClick={() => workspace.toggleStar(node)}
                      className={[
                        'absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full hover:bg-surface-3',
                        starred
                          ? 'text-accent'
                          : 'text-muted sm:opacity-0 sm:focus-visible:opacity-100 sm:group-hover:opacity-100',
                      ].join(' ')}
                    >
                      <Icon name="star" className="h-4 w-4" filled={starred} />
                    </button>
                  );
                })()}
              </div>
            ))}
          </section>
        ))}
    </>
  );
}
